import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';

const API_CYCLIC_TASKS_URL = `${API_BASE_URL}/cyclictasktemplates`;

function toIntOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

export default function CyclicTasksPanel({ onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editTask, setEditTask] = useState(null);

  // Add form
  const [newName, setNewName] = useState('');
  const [newPredelay, setNewPredelay] = useState('0');
  const [newDayTimespan, setNewDayTimespan] = useState('1');
  const [addError, setAddError] = useState(null);

  // Edit form
  const [editName, setEditName] = useState('');
  const [editPredelay, setEditPredelay] = useState('0');
  const [editDayTimespan, setEditDayTimespan] = useState('1');
  const [editError, setEditError] = useState(null);

  const [menuTaskId, setMenuTaskId] = useState(null);
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState(null);

  const fetchTasks = () => {
    setLoading(true);
    fetch(API_CYCLIC_TASKS_URL, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch cyclic tasks');
        return res.json();
      })
      .then((data) => {
        setTasks(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [tasks]);

  const resetAddForm = () => {
    setNewName('');
    setNewPredelay('0');
    setNewDayTimespan('1');
    setAddError(null);
  };

  const openEdit = (task) => {
    setEditTask(task);
    setEditName(task?.name ?? '');
    setEditPredelay(String(task?.predelay ?? 0));
    setEditDayTimespan(String(task?.dayTimespan ?? 1));
    setEditError(null);
    setShowEditPanel(true);
  };

  const handleAdd = () => {
    setAddError(null);
    const name = (newName || '').trim();
    const predelay = toIntOrNull(newPredelay);
    const dayTimespan = toIntOrNull(newDayTimespan);

    if (!name) {
      setAddError('Podaj nazwę.');
      return;
    }
    if (predelay === null || predelay < 0) {
      setAddError('predelay musi być liczbą całkowitą >= 0.');
      return;
    }
    if (dayTimespan === null || dayTimespan <= 0) {
      setAddError('dayTimespan musi być liczbą całkowitą > 0.');
      return;
    }

    fetch(API_CYCLIC_TASKS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        name,
        predelay,
        dayTimespan
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Nie udało się dodać cyclic task');
        return res.json();
      })
      .then(() => {
        setShowAddPanel(false);
        resetAddForm();
        fetchTasks();
      })
      .catch((err) => setAddError(err.message));
  };

  const handleEditSave = () => {
    if (!editTask) return;
    setEditError(null);

    const name = (editName || '').trim();
    const predelay = toIntOrNull(editPredelay);
    const dayTimespan = toIntOrNull(editDayTimespan);

    if (!name) {
      setEditError('Podaj nazwę.');
      return;
    }
    if (predelay === null || predelay < 0) {
      setEditError('predelay musi być liczbą całkowitą >= 0.');
      return;
    }
    if (dayTimespan === null || dayTimespan <= 0) {
      setEditError('dayTimespan musi być liczbą całkowitą > 0.');
      return;
    }

    fetch(`${API_CYCLIC_TASKS_URL}/${editTask.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        id: editTask.id,
        name,
        predelay,
        dayTimespan
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Nie udało się zapisać zmian');
        return res.json();
      })
      .then(() => {
        setShowEditPanel(false);
        setEditTask(null);
        fetchTasks();
      })
      .catch((err) => setEditError(err.message));
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '700px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>

      <h2 style={{ marginBottom: 16 }}>Cyclic tasks</h2>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #6cf',
            background: '#eaf6ff',
            color: '#227',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => { setShowAddPanel(true); setShowEditPanel(false); setMenuTaskId(null); setConfirmDeleteTaskId(null); }}
        >➕ Dodaj</button>
      </div>

      {loading && <div>Ładowanie...</div>}
      {error && <div style={{ color: 'red' }}>Błąd: {error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ background: '#f8f8f8' }}>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Name</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>predelay</th>
            <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>dayTimespan</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedTasks.map((t) => (
            <tr key={t.id ?? `${t.name}-${t.predelay}-${t.dayTimespan}`} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}>{t.name}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{t.predelay}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{t.dayTimespan}</td>
              <td style={{ padding: '8px', textAlign: 'center', position: 'relative' }}>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 22,
                    cursor: 'pointer',
                    color: '#888',
                    marginLeft: 8
                  }}
                  aria-label="Menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuTaskId(t.id);
                  }}
                >⋯</button>

                {menuTaskId === t.id && (
                  <div style={{
                    position: 'absolute',
                    background: '#fff',
                    border: '1px solid #eee',
                    borderRadius: 8,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    padding: 12,
                    zIndex: 12000,
                    right: 24,
                    top: 40
                  }}>
                    <button
                      style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#227' }}
                      onClick={() => { setMenuTaskId(null); openEdit(t); }}
                    >Edit</button>
                    <button
                      style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#a22' }}
                      onClick={() => { setMenuTaskId(null); setConfirmDeleteTaskId(t.id); }}
                    >Delete</button>
                    <button
                      style={{ display: 'block', width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
                      onClick={() => setMenuTaskId(null)}
                    >Cancel</button>
                  </div>
                )}

                {confirmDeleteTaskId === t.id && (
                  <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%,-50%)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                    padding: 28,
                    zIndex: 13000,
                    minWidth: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 16
                  }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Usunąć <b>{t.name}</b>?</div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <button
                        onClick={() => {
                          fetch(`${API_CYCLIC_TASKS_URL}/${t.id}`, {
                            method: 'DELETE',
                            headers: {
                              ...(authToken ? { 'Authorization': authToken } : {})
                            }
                          })
                            .then((res) => {
                              if (!res.ok) throw new Error('Failed to delete');
                              setConfirmDeleteTaskId(null);
                              fetchTasks();
                            })
                            .catch((err) => {
                              setError(err.message);
                              setConfirmDeleteTaskId(null);
                            });
                        }}
                        style={{
                          padding: '10px 24px',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: 8,
                          border: '1px solid #f66',
                          background: '#fff0f0',
                          color: '#a22',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                      >Usuń</button>
                      <button
                        onClick={() => setConfirmDeleteTaskId(null)}
                        style={{
                          padding: '10px 24px',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: 8,
                          border: '1px solid #bbb',
                          background: '#f8f8f8',
                          color: '#555',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                      >Anuluj</button>
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && sortedTasks.length === 0 && <div>Brak cyclic tasks.</div>}

      {showAddPanel && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 24,
          zIndex: 14000,
          width: '460px',
          maxWidth: '95vw'
        }}>
          <h3 style={{ marginTop: 0 }}>Dodaj cyclic task</h3>
          {addError && <div style={{ color: 'red', marginBottom: 12 }}>{addError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Name</div>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>predelay</div>
              <input value={newPredelay} onChange={(e) => setNewPredelay(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>dayTimespan</div>
              <input value={newDayTimespan} onChange={(e) => setNewDayTimespan(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={handleAdd}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 8,
                border: '1px solid #6cf',
                background: '#eaf6ff',
                color: '#227',
                cursor: 'pointer'
              }}
            >Zapisz</button>
            <button
              onClick={() => { setShowAddPanel(false); resetAddForm(); }}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 8,
                border: '1px solid #bbb',
                background: '#f8f8f8',
                color: '#555',
                cursor: 'pointer'
              }}
            >Anuluj</button>
          </div>
        </div>
      )}

      {showEditPanel && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 24,
          zIndex: 14000,
          width: '460px',
          maxWidth: '95vw'
        }}>
          <h3 style={{ marginTop: 0 }}>Edytuj cyclic task</h3>
          {editError && <div style={{ color: 'red', marginBottom: 12 }}>{editError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Name</div>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>predelay</div>
              <input value={editPredelay} onChange={(e) => setEditPredelay(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>dayTimespan</div>
              <input value={editDayTimespan} onChange={(e) => setEditDayTimespan(e.target.value)} style={{ width: '100%', padding: 8 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              onClick={handleEditSave}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 8,
                border: '1px solid #6cf',
                background: '#eaf6ff',
                color: '#227',
                cursor: 'pointer'
              }}
            >Zapisz</button>
            <button
              onClick={() => { setShowEditPanel(false); setEditTask(null); }}
              style={{
                flex: 1,
                padding: '10px 0',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 8,
                border: '1px solid #bbb',
                background: '#f8f8f8',
                color: '#555',
                cursor: 'pointer'
              }}
            >Anuluj</button>
          </div>
        </div>
      )}
    </div>
  );
}
